<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="product.exists() ? t('club_product_title_update') : t('club_product_title_create')"
            back="/club-products"
        />

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_name') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="product.name"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('name')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="product.name"
                                    :items="product.translations['name']"
                                    @touch="(v) => product.translations['name'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_manufacturer') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="product.manufacturer"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('manufacturer')"
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_price') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="product.price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('price')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions field-note">{{ t('club_product_hint_price') }}</div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_currency') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="product.currency"
                                    :items="[ { text: '-', value: '' } ].concat(currencies())"
                                    outlined
                                    dense
                                    append-icon="mdi-chevron-down"
                                    :error-messages="form.errors.get('currency')"
                                ></v-select>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_description') }}</div>
                            <div class="horizontal-input">
                                <v-textarea
                                    v-model="product.description"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('description')"
                                ></v-textarea>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="product.description"
                                    :items="product.translations['description']"
                                    @touch="(v) => product.translations['description'] = v"
                                    text
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_icon') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="product.photo"
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    :append-outer-icon="product.photoUrl ? 'mdi-eye-outline' : ''"
                                    dense
                                    :error-messages="photoForm.errors.get('photo')"
                                    @click:append-outer="viewPhoto(product.photoUrl)"
                                    @click:clear="product.photo = undefined"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal" v-if="product.photos && product.photos.length">
                            <v-sheet
                                v-for="(photo, i) in product.photos"
                                :key="photo"
                                color="white"
                                elevation="0"
                                height="200"
                                width="200"
                                class="float-left mr-8 mb-8 bordered position-relative"
                            >
                                <v-img
                                    :lazy-src="photo"
                                    :src="photo"
                                    max-height="150"
                                    max-width="150"
                                    class="mx-auto mt-6"
                                ></v-img>
                                <confirm-modal
                                    :title="t('general_title_confirm_action')"
                                    @agree="product.delPhoto(i)"
                                >
                                    <template #default="{ on, attrs }">
                                        <v-btn
                                            v-on="on"
                                            v-bind="attrs"
                                            plain
                                            icon
                                            absolute
                                            top
                                            right
                                            class="mt-n5 mr-n5"
                                        ><v-icon color="orange">mdi-close</v-icon></v-btn>
                                    </template>
                                </confirm-modal>
                                <v-btn
                                    v-if="i !== 0"
                                    @click="product.updatePhotoNumber(i, (i - 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    left
                                    class="mb-n5 ml-n5"
                                ><v-icon color="customized-primary">mdi-arrow-left</v-icon></v-btn>
                                <v-btn
                                    v-if="i !== product.photos.length - 1"
                                    @click="product.updatePhotoNumber(i, (i + 1))"
                                    plain
                                    icon
                                    absolute
                                    bottom
                                    right
                                    class="mb-n5 mr-n5"
                                ><v-icon color="customized-primary">mdi-arrow-right</v-icon></v-btn>
                            </v-sheet>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_header_photos') }}</div>
                            <div class="horizontal-input">
                                <v-file-input
                                    v-model="product.photoUps"
                                    outlined
                                    prepend-icon=""
                                    prepend-inner-icon="mdi-paperclip"
                                    dense
                                    multiple
                                    :error-messages="photoForm.errors.get('photos.0') || photoForm.errors.get('photos')"
                                    @click:clear="product.photoUps = undefined"
                                ></v-file-input>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_link_text') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="product.linkText"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link_text')"
                                ></v-text-field>
                            </div>
                            <div class="horizontal-actions">
                                <edit-translations-modal
                                    v-model="product.linkText"
                                    :items="product.translations['link_text']"
                                    @touch="(v) => product.translations['link_text'] = v"
                                ></edit-translations-modal>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('club_product_label_link_mode') }}</div>
                            <div class="horizontal-input pb-6">
                                <v-btn-toggle
                                    v-model="linkMode"
                                    active-class="primary"
                                    class="my-1"
                                    dense
                                    mandatory
                                >
                                    <v-btn
                                        value="external"
                                        class="exs mr-3"
                                        small
                                    >{{ t('club_product_label_link_external') }}</v-btn>
                                    <v-btn
                                        value="dynamic"
                                        class="exs"
                                        small
                                    >{{ t('club_product_label_link_dynamic') }}</v-btn>
                                </v-btn-toggle>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t(linkMode === 'external' ? 'club_product_label_link_url' : 'club_product_label_link_params') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-if="linkMode === 'external'"
                                    v-model="product.link"
                                    type="text"
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('link')"
                                ></v-text-field>
                                <template v-else>
                                    <div
                                        v-for="(row, i) in linkParamRows"
                                        :key="`param_${i}`"
                                        class="d-flex mb-2"
                                    >
                                        <v-text-field
                                            v-model="row.key"
                                            :placeholder="t('club_product_label_param_key')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-text-field
                                            v-model="row.value"
                                            :placeholder="t('club_product_label_param_value')"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2"
                                        ></v-text-field>
                                        <v-btn
                                            @click="linkParamRows.splice(i, 1)"
                                            icon
                                            class="mt-1"
                                        ><v-icon>mdi-close</v-icon></v-btn>
                                    </div>
                                    <v-btn
                                        @click="linkParamRows.push({ key: '', value: '' })"
                                        v-ripple="false"
                                        plain
                                        color="orange darken-4"
                                        class="add mb-4 ml-1"
                                    ><v-icon left size="16">mdi-plus-circle</v-icon>{{ t('club_product_action_add_param') }}</v-btn>
                                </template>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('general_action_published') }}</div>
                            <div class="horizontal-input">
                                <v-switch
                                    class="ma-0"
                                    v-model="product.enabled"
                                ></v-switch>
                            </div>
                        </div>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4">
                    <v-btn
                        :to="{ name: 'club-products' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prevent="submit"
                    >{{ product.exists() ? t('general_action_save') : t('club_product_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { ClubProduct } from '@/core/models/club-product'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'
    import ConfirmModal from '@/components/ConfirmModal.vue'

    export default defineComponent({
        components: {
            EditTranslationsModal,
            ConfirmModal,
        },

        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id'] || undefined

            const state = reactive({
                overlay: true,
                linkParamRows: <{ key: string, value: string }[]>[],
                linkMode: 'external',
            })

            const productModel = computed(() => <ClubProduct>store.getters['clubProduct/get'])

            const form = reactive(useForm())
            const photoForm = reactive(useForm())

            watch(() => state.linkMode, (mode) => {
                if (mode === 'dynamic' && ! state.linkParamRows.length) {
                    state.linkParamRows.push({ key: '', value: '' })
                }
            })

            const capture = () => {
                store.dispatch('clubProduct/getOrNew', { id: id, payload: { append: 'translations' } }).then(() => {
                    state.linkParamRows = Object.entries(productModel.value.linkParams || {}).map(([key, value]) => ({ key, value: <string>value }))
                    state.linkMode = state.linkParamRows.length > 0 ? 'dynamic' : 'external'
                    state.overlay = false
                })
            }

            const submit = () => {
                productModel.value.linkParams = state.linkMode === 'dynamic'
                    ? state.linkParamRows.filter((row) => row.key).reduce((acc: any, row) => { acc[row.key] = row.value; return acc }, {})
                    : {}

                form.usingSubmit(store.dispatch('clubProduct/updateOrCreate', {
                    id: id,
                    payload: productModel.value.payload()
                }))
                    .then(() => {
                        const savedId = <any>productModel.value.id
                        const next = () => router.push({ name: 'club-products.view', params: <any>{ id: savedId } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        if (productModel.value.photo || productModel.value.photoUps) {
                            const data = new FormData()
                            if (productModel.value.photo) {
                                data.append('photo', productModel.value.photo)
                            }
                            if (productModel.value.photoUps) {
                                productModel.value.photoUps.map((i) => data.append('photos[]', i))
                            }
                            photoForm.usingSubmit(store.dispatch('clubProduct/photo', { id: savedId, payload: data }).then(() => {
                                next()
                            }))
                        } else {
                            next()
                        }
                    })
            }

            const viewPhoto = (url: string) => {
                window.open(url)
            }

            onMounted(() => {
                capture()
            })

            return {
                form,
                photoForm,
                capture,
                viewPhoto,
                submit,
                product: productModel.value,
                ...useI18n(),
                ...useEnums(),
                ...toRefs(state)
            }
        }
    })
</script>
